/**
 * Admin Dashboard
 * Landing page for administration functions
 * Requires admin permissions to access
 */

import { Link } from 'react-router-dom';
import { Settings, Users, Shield, Database, Lock, Plug } from 'lucide-react';
import { useUserStore } from '../../stores/prioritization/userStore';

export default function AdminDashboard() {
  const canAny = useUserStore((state) => state.canAny);
  
  // Check if user has any admin permissions
  const hasAdminAccess = canAny([
    'admin.users',
    'admin.groups', 
    'admin.roles',
    'admin.policies',
    'admin.settings'
  ]);
  
  if (!hasAdminAccess) {
    return (
      <div className="space-y-6">
        <div className="card bg-[var(--coral-dim)] border-[var(--coral)]">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-[var(--coral-dim)] rounded-full">
              <Lock className="h-6 w-6 text-[var(--coral)]" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-[var(--coral)] mb-2">Access Denied</h2>
              <p className="text-sm text-[var(--coral)] mb-2">
                You don't have permission to access the Administration area. This requires admin-level permissions.
              </p>
              <p className="text-xs text-[var(--coral)]">
                Only Platform Administrators can access this section.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  const adminSections = [
    {
      name: 'Users',
      description: 'Manage user accounts and permissions',
      href: '/prioritization/admin/users',
      icon: Users,
      color: 'green',
      comingSoon: false
    },
    {
      name: 'Groups',
      description: 'Manage groups and memberships',
      href: '/prioritization/admin/groups',
      icon: Users,
      color: 'blue',
      comingSoon: false
    },
    {
      name: 'Roles & Permissions',
      description: 'Configure access control and role definitions',
      href: '/prioritization/admin/roles',
      icon: Shield,
      color: 'purple',
      comingSoon: false
    },
    {
      name: 'Enterprise Identity',
      description: 'SSO providers (Okta, Azure AD) and org hierarchy',
      href: '/prioritization/admin/identity',
      icon: Shield,
      color: 'sky',
      comingSoon: false
    },
    {
      name: 'Connectors',
      description: 'Connect external tools (Miro, Jira, Slack)',
      href: '/prioritization/settings/connectors',
      icon: Plug,
      color: 'indigo',
      comingSoon: false
    },
    {
      name: 'System Settings',
      description: 'System-wide configuration and preferences',
      href: '/prioritization/admin/settings',
      icon: Settings,
      color: 'gray',
      comingSoon: true
    },
    {
      name: 'Data Management',
      description: 'Database operations and data exports',
      href: '/prioritization/admin/data',
      icon: Database,
      color: 'orange',
      comingSoon: true
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-[var(--text-pri)]">Administration</h1>
        <p className="mt-2 text-[var(--text-sec)]">
          Platform administration and configuration
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {adminSections.map((section) => {
          const Icon = section.icon;
          const isExternal = section.href.startsWith('/admin/');
          
          const content = (
            <div
              className={`bg-[var(--bg-raised)] border-2  p-6 transition-all ${
                section.comingSoon
                  ? 'border-[var(--rule)] opacity-60 cursor-not-allowed'
                  : 'border-[var(--rule)] hover:border-[var(--slate)] hover:shadow-md cursor-pointer'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3  bg-${section.color}-100`}>
                  <Icon className={`h-6 w-6 text-${section.color}-600`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-[var(--text-pri)]">
                      {section.name}
                    </h3>
                    {section.comingSoon && (
                      <span className="px-2 py-1 text-xs font-medium bg-[var(--hover-tint)] text-[var(--text-sec)] rounded">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm text-[var(--text-sec)]">
                    {section.description}
                  </p>
                </div>
              </div>
            </div>
          );

          if (section.comingSoon) {
            return <div key={section.name}>{content}</div>;
          }

          if (isExternal) {
            return (
              <a
                key={section.name}
                href={section.href}
                className="block"
              >
                {content}
              </a>
            );
          }

          return (
            <Link
              key={section.name}
              to={section.href}
              className="block"
            >
              {content}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

